<template>
  <div v-if="show" class="ppv-modal-overlay" @click.self="handleClose">
    <div class="ppv-older-modal">

      <PpvModalHeader
        :title="$t('ppv.older_visits.title')"
        :subtitle="$t('ppv.older_visits.subtitle')"
        @close="handleClose"
      />

      <div class="ppv-older-modal-body">
        <p class="ppv-description">
          {{ $t("ppv.older_visits.description") }}
        </p>

        <div class="ppv-input-group">
          <label>{{ $t("ppv.older_visits.identifier_label") }}</label>
          <div class="ppv-input-wrap">
            <input
              v-model="identifier"
              type="text"
              :placeholder="$t('ppv.older_visits.identifier_placeholder')"
            >
          </div>
        </div>

        <div class="ppv-divider">
          <span>{{ $t("ppv.older_visits.divider_text") }}</span>
        </div>

        <PpvDateRangeInput
          :start-date="startDate"
          :end-date="endDate"
          :max-end-date="maxEndDate"
          :error="validationError"
        />

        <div class="ppv-status-bar">
          <div class="ppv-status-left">
            <v-icon class="mr-2">{{ icons.mdiWifi }}</v-icon>
            {{ $t("ppv.older_visits.connection_status") }}
          </div>
          <span :class="isOnline ? 'ppv-status-online' : 'ppv-status-offline'">
            {{ isOnline ? $t('ppv.older_visits.online') : $t('ppv.older_visits.offline') }}
          </span>
        </div>
      </div>

      <PpvModalFooter
        :confirm-label="$t('ppv.older_visits.btn_search')"
        :is-valid="isValidSearch"
        @cancel="handleClose"
        @confirm="handleSearch"
      />
    </div>
  </div>
</template>

<script>
import icons from '@/mixins/icons'
import { EVENTS, PVI_PANEL_NAME } from '@/plugins/analytics/events'
import { useOlderVisitsSearch } from './composables/useOlderVisitsSearch'
import { useDateRangeValidation } from './composables/useDateRangeValidation'
import PpvModalHeader from './shared/PpvModalHeader.vue'
import PpvModalFooter from './shared/PpvModalFooter.vue'
import PpvDateRangeInput from './shared/PpvDateRangeInput.vue'

const { calcMaxEndDate, validate } = useDateRangeValidation(90)

export default {
  name: 'PpvOlderVisitsModal',
  components: {
    PpvModalHeader,
    PpvModalFooter,
    PpvDateRangeInput,
  },
  mixins: [icons],
  props: {
    show: { type: Boolean, required: true },
    mockVisits: { type: Array, default: () => [] },
  },
  emits: ['close', 'search'],
  data() {
    return {
      identifier: '',
      startDate: '',
      endDate: '',
    }
  },
  computed: {
    hasIdentifier() {
      return this.identifier.trim().length > 0
    },
    hasDateRange() {
      return this.startDate && this.endDate
    },
    parsedFrom() {
      return this.startDate ? new Date(this.startDate) : null
    },
    parsedTo() {
      return this.endDate ? new Date(this.endDate) : null
    },
    maxEndDate() {
      return calcMaxEndDate(this.startDate)
    },
    validationError() {
      const errorKey = validate(this.parsedFrom, this.parsedTo)

      return errorKey ? this.$t(errorKey) : ''
    },
    isValidSearch() {
      const hasValidDate = this.hasDateRange && !this.validationError

      return this.hasIdentifier || hasValidDate
    },
    isOnline() {
      return navigator.onLine
    },
  },
  watch: {
    show(val) {
      if (val) this.resetForm()
    },
  },
  methods: {
    resetForm() {
      this.identifier = ''
      this.startDate = ''
      this.endDate = ''
    },
    handleClose() {
      this.$emit('close')
    },
    handleSearch() {
      if (!this.isValidSearch) {
        return
      }

      const { filterByParams } = useOlderVisitsSearch(this.mockVisits)

      const results = filterByParams({
        identifier: this.hasIdentifier ? this.identifier : null,
        startDate: this.hasDateRange && !this.validationError ? new Date(this.startDate) : null,
        endDate: this.hasDateRange && !this.validationError ? new Date(this.endDate) : null,
      })

      this.$track(EVENTS.PV_OLDER_SEARCH_ATTEMPT, {
        has_identifier: this.hasIdentifier,
        has_date_range: this.hasDateRange && !this.validationError,
        online_status: navigator.onLine,
        result_count: results.length,
        panel_name: PVI_PANEL_NAME,
      })

      this.$emit('search', results)
    }
  }
}
</script>

<style scoped>
.ppv-modal-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  backdrop-filter: blur(2px);
}
.ppv-older-modal {
  background: #fff;
  width: calc(100% - 32px);
  max-width: 540px;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  animation: modalFade 0.2s ease;
}
.ppv-older-modal-body {
  padding: 32px;
  max-height: 80vh;
  overflow-y: auto;
}
.ppv-description {
  font-size: 13px;
  color: #718096;
  line-height: 1.6;
  margin-bottom: 24px;
}
.ppv-input-group {
  margin-bottom: 20px;
}
.ppv-input-group label {
  display: block;
  font-size: 13px;
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 12px;
}
.ppv-input-wrap input {
  width: 100%;
  padding: 10px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  outline: none;
  color: #1a2340;
  transition: border-color 0.2s, box-shadow 0.2s;
}
.ppv-input-wrap input::placeholder {
  color: #a0aec0;
}
.ppv-divider {
  display: flex;
  align-items: center;
  text-align: center;
  margin: 24px 0;
  color: #e2e8f0;
}
.ppv-divider::before,
.ppv-divider::after {
  content: '';
  flex: 1;
  border-bottom: 1px solid #e2e8f0;
}
.ppv-divider span {
  padding: 0 10px;
  font-size: 11px;
  font-weight: 700;
  color: #cbd5e0;
}
.ppv-status-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 20px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  margin-top: 12px;
}
.ppv-status-left {
  font-size: 13px;
  color: #4a5568;
  display: flex;
  align-items: center;
  gap: 8px;
}
.ppv-status-left .v-icon {
  color: #4b91ac !important;
  font-size: 16px !important;
}
.ppv-status-online {
  font-size: 13px;
  font-weight: 600;
  color: #38a169;
}
.ppv-status-offline {
  font-size: 13px;
  font-weight: 600;
  color: #e53e3e;
}
@keyframes modalFade {
  from { opacity: 0; transform: translateY(-8px); }
  to   { opacity: 1; transform: translateY(0);    }
}
</style>
